import {
  Actions,
  AmountCell,
  Badge,
  ChevronDownIcon,
  ClipboardIcon,
  CountCell,
  EyeIcon,
  formatNumber,
  formatTimeDate,
  initFlowbite,
  Link,
  modalHandler,
  NameCell,
  PhoneCell,
  SubduedCell,
  success,
  ToggleActiveStatus,
  ToggleArchiveStatus,
  Tooltip,
  useEffect,
} from "@";

const isTotalsRow = (row) => row?.id === "customRow";

const copyLink = (link) => {
  navigator.clipboard.writeText(link);
  success("تم النسخ");
};

const copyOrderIds = (ids) => {
  navigator.clipboard.writeText(ids.join(", "));
  success("تم نسخ جميع الأرقام");
};

const CartsDropdown = ({ cartIds, rowId }) => {
  const dropdownId = `orders-dropdown-${rowId}`;
  const buttonId = `orders-dropdown-btn-${rowId}`;

  useEffect(() => {
    const t = setTimeout(() => initFlowbite(), 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <button
        id={buttonId}
        data-dropdown-toggle={dropdownId}
        className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-[12px] font-bold tabular-nums text-purple-900 ring-1 ring-purple-100 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
        type="button"
      >
        {formatNumber(cartIds.length)}
        <ChevronDownIcon className="h-3 w-3" />
      </button>

      <div
        id={dropdownId}
        className="z-50 hidden w-52 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl shadow-purple-900/5 dark:border-gray-700 dark:bg-gray-800 dark:shadow-black/30"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {formatNumber(cartIds.length)} طلب
          </span>
          <button
            onClick={() => copyOrderIds(cartIds)}
            className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-100 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
          >
            <ClipboardIcon className="h-3 w-3" />
            نسخ الكل
          </button>
        </div>
        <div className="max-h-48 overflow-y-auto p-2">
          <div className="flex flex-wrap gap-1">
            {cartIds.map((orderId) => (
              <Link
                key={orderId}
                to={`/donations?toDate=&fromDate=&id=${orderId}`}
                className="rounded-md bg-purple-50 px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-purple-900 ring-1 ring-purple-100 hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
              >
                {orderId}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// Affiliate identity — bold name on top, phone + email muted underneath.
const AffiliateCell = ({ name, phone, email }) => (
  <div className="flex flex-col leading-tight">
    <NameCell name={name} />
    <PhoneCell phone={phone} />
    {email && (
      <span className="text-[11px] text-gray-500 dark:text-gray-500">
        {email}
      </span>
    )}
  </div>
);

// Two-line title + clickable link, with truncation. Title sits in a soft tint,
// link is the same chip but reads as interactive (hover purple).
const TitleLinkCell = ({ title, link }) => (
  <div className="flex flex-col gap-1.5 py-1">
    <p
      title={title}
      className="rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700/40 dark:text-gray-300"
    >
      {title?.length > 30 ? title.slice(0, 30) + "…" : title}
    </p>
    <button
      type="button"
      title={link}
      onClick={() => copyLink(link)}
      className="rounded-md bg-purple-50 px-2 py-1 text-start font-mono text-[11px] font-medium text-purple-900 ring-1 ring-purple-100 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
    >
      {link?.length > 60 ? link.slice(0, 60) + "…" : link}
    </button>
  </div>
);

// Status — keep both badges on this page since archive state matters here.
const StatusCell = ({ isActive, isArchive }) => (
  <div className="flex flex-col gap-2">
    <Badge
      title={isActive ? "مفعل" : "غير مفعل"}
      color={isActive ? "green" : "red"}
    />
    {isArchive && <Badge title="مؤرشف" color="dark" />}
  </div>
);

// Totals-row label — gradient pill matching QualityStatistics' total row style.
const TotalsLabel = ({ value }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-l from-purple-50 to-purple-100/60 px-3 py-1 text-sm font-bold tracking-tight text-purple-900 ring-1 ring-purple-200 dark:from-purple-950/50 dark:to-purple-950/30 dark:text-purple-300 dark:ring-purple-800/40">
    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 dark:bg-purple-400" />
    {value}
  </span>
);

export const TableActions = ({
  row,
  reset,
  setIsEdit,
  ToggleActive,
  deleteHandler,
  ToggleArchive,
}) => {
  const edit = (row) => {
    reset(row);
    setIsEdit(true);
    modalHandler();
  };

  return (
    <Actions notShow row={row} edit={edit} delete={deleteHandler}>
      <Link
        data-tooltip-placement="right"
        to={`/affiliateLinks/${row.id}`}
        className="icon-style cursor-pointer"
        data-tooltip-target={`action-z-${row.id}`}
      >
        <EyeIcon className="w-6" />
      </Link>
      <Tooltip
        className="dark:bg-gray-600"
        title="عرض"
        id={`action-z-${row.id}`}
      />

      <ToggleActiveStatus row={row} click={ToggleActive} />
      <ToggleArchiveStatus row={row} click={ToggleArchive} />
    </Actions>
  );
};

// TanStack columns — preserves the `customRow` totals row pattern so the last
// row keeps its bold "إجمالي" treatment.
export const tableColumns = [
  {
    id: "affiliateName",
    header: "المعلن",
    cell: ({ row }) =>
      isTotalsRow(row.original) ? (
        <TotalsLabel value={row.original.affiliateName} />
      ) : (
        <AffiliateCell
          name={row.original.affiliateName}
          phone={row.original.affiliatePhone}
          email={row.original.affiliateEmail}
        />
      ),
  },
  {
    size: 300,
    id: "titleLink",
    header: "العنوان / الرابط",
    cell: ({ row }) =>
      isTotalsRow(row.original) ? null : (
        <TitleLinkCell
          title={row.original.title}
          link={row.original.affiliateFullLink}
        />
      ),
  },
  {
    id: "numberOfVisits",
    header: "الزيارات",
    size: 100,
    cell: ({ row }) =>
      isTotalsRow(row.original) ? (
        <AmountCell
          value={row.original.numberOfVisits}
          tone="purple"
          currency=""
        />
      ) : (
        <CountCell value={row.original.numberOfVisits} />
      ),
  },
  {
    id: "numberOfDonations",
    header: "التبرعات",
    size: 100,
    cell: ({ row }) =>
      isTotalsRow(row.original) ? (
        <AmountCell
          value={row.original.numberOfDonations}
          tone="purple"
          currency=""
        />
      ) : (
        <CountCell value={row.original.numberOfDonations} />
      ),
  },
  {
    id: "cartIds",
    header: "الطلبات",
    size: 110,
    cell: ({ row }) => {
      if (isTotalsRow(row.original)) return null;
      const cartIds = row.original.cartIds || [];
      if (cartIds.length === 0) {
        return <SubduedCell value="" />;
      }
      return <CartsDropdown cartIds={cartIds} rowId={row.original.id} />;
    },
  },
  {
    id: "totalDonationAmount",
    header: "المبلغ",
    size: 120,
    cell: ({ row }) =>
      isTotalsRow(row.original) ? (
        <AmountCell value={row.original.totalDonationAmount} tone="purple" />
      ) : (
        <AmountCell value={row.original.totalDonationAmount} tone="emerald" />
      ),
  },
  {
    id: "createdOn",
    header: "التاريخ",
    size: 150,
    cell: ({ row }) =>
      isTotalsRow(row.original) ? null : (
        <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
          {formatTimeDate(row.original.createdOn)}
        </span>
      ),
  },
  {
    size: 110,
    id: "status",
    header: "الحالة",
    cell: ({ row }) =>
      isTotalsRow(row.original) ? null : (
        <StatusCell
          isActive={row.original.isActive}
          isArchive={row.original.isArchive}
        />
      ),
  },
];
